import {
  createContext,
  Dispatch,
  FC,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import {
  BranchOfficeDto,
  BranchOfficeRequestDto,
  BranchOfficeService,
} from '@/_generatedApi';
import BranchOfficeForm, {
  BranchOfficeFormOutputValues,
} from '@/components/settings-branch-offices/SettingsBranchOfficeForm';
import CDataGrid, { ColDef } from '@/components/ui/common/data-grid/CDataGrid';
import Modal from '@/components/ui/common/modal/CModal';
import { Option } from '@/components/ui/fields/types';
import BranchOfficesFilters, {
  BranchOfficeFilter,
} from '@/components/ui/filters/BranchOfficesFilters';
import { ITEMS_PER_PAGE, MAX_TAKE_ITEMS } from '@/constants/pagination';
import { useShowToast } from '@/hooks/use-show-toast';
import { updateFilterData, updateObjectInArray } from '@/utils/immutable';
import { getSkipCount } from '@/utils/request';
import { nullFieldsToUndefined } from '@/utils/validation';

const fetchAllBranchOffices = async () => {
  return (
    await BranchOfficeService.getBranchOfficeList({
      sortBy: 'name',
      descending: false,
      skip: 0,
      take: MAX_TAKE_ITEMS,
    })
  ).data as BranchOfficeDto[];
};

const fetchBranchOfficesForPage = async (
  page: number,
  itemsPerPage: number,
  filter: BranchOfficeFilter
) => {
  const skip = getSkipCount(page, itemsPerPage);

  return await BranchOfficeService.getBranchOfficeList({
    sortBy: 'name',
    descending: false,
    skip,
    take: itemsPerPage,
    ...filter,
  });
};

export const BranchOfficeContext = createContext<Option[] | null>(null);

const SettingsBranchOfficePage: FC = () => {
  const { t } = useTranslation();
  const { showToast, showGenericErrorToast } = useShowToast();
  const [showModal, setShowModal] = useState(false);
  const [countOfBranchOffices, setCountOfBranchOffices] = useState(0);
  const [branchOffices, setBranchOffices] = useState<BranchOfficeDto[]>([]);
  const [branchOfficesAll, setBranchOfficesAll] = useState<Option[]>([]);
  const [rowsPerPage, setRowsPerPage] = useState(ITEMS_PER_PAGE);

  const [chosenBranchOffice, setChosenBranchOffice] =
    useState<BranchOfficeDto | null>(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [filter, setFilter] = useState<BranchOfficeFilter>({});

  const branchOfficeSettingsColumnsDef: ColDef<BranchOfficeDto>[] = [
    { key: 'name', nested: false, isDate: false, sortable: true },
    { key: 'city', nested: false, isDate: false, sortable: true },
    { key: 'comment', nested: false, isDate: false, sortable: false },
    {
      key: 'superiorBranchOffice',
      nested: true,
      isDate: false,
      sortable: true,
    },
  ];

  const fetchData = useCallback(async () => {
    try {
      const filteredData = await fetchBranchOfficesForPage(
        currentPage,
        rowsPerPage,
        filter
      );
      setBranchOffices(filteredData.data as BranchOfficeDto[]);
      setCountOfBranchOffices(filteredData.metadata?.total || 0);
    } catch (e) {
      showGenericErrorToast();
    }
  }, [currentPage, filter, rowsPerPage, showGenericErrorToast]);

  const handleAddBranchOffice = () => {
    setChosenBranchOffice(null);
    setShowModal(true);
  };

  const handleEditBranchOffice = (branchOffice: BranchOfficeDto) => {
    if (!branchOffice) return;
    setChosenBranchOffice(branchOffice);
    setShowModal(true);
  };

  const handleChangePage = (page: number) => {
    setCurrentPage(page);
  };

  const handleSubmit = async (
    values: BranchOfficeFormOutputValues,
    id?: number
  ) => {
    const valuesWithoutNullFields = nullFieldsToUndefined(values);
    if (id) {
      try {
        const data = (
          await BranchOfficeService.putBranchOfficeUpdate({
            id,
            requestBody: valuesWithoutNullFields as BranchOfficeRequestDto,
          })
        ).data;

        if (data) {
          setBranchOffices(
            (branchOffices) =>
              updateObjectInArray(branchOffices, data) as BranchOfficeDto[]
          );
        }

        setChosenBranchOffice(null);
        setShowModal(false);

        showToast('success', t('notifications.itemWasSuccessfullyEdited'));
      } catch (e) {
        showGenericErrorToast();
      }
    } else {
      try {
        const data = (
          await BranchOfficeService.postBranchOfficeCreate({
            requestBody: valuesWithoutNullFields as BranchOfficeRequestDto,
          })
        ).data;

        if (data) {
          fetchData();

          setBranchOfficesAll((branchOffices) => [
            ...branchOffices,
            { label: data.name, value: data.id || 0 },
          ]);
        }

        setShowModal(false);
        showToast('success', t('notifications.itemWasSuccessfullyCreated'));
      } catch (e) {
        showGenericErrorToast();
      }
    }
  };

  const handleDeleteBranchOffice = async (
    id: number,
    setShowModal: Dispatch<SetStateAction<boolean>>
  ) => {
    if (!id) return;

    try {
      await BranchOfficeService.deleteBranchOfficeDelete({ id });

      if (branchOffices.length === 1 && currentPage !== 1) {
        setCurrentPage((page) => page - 1);
      }

      fetchData();

      showToast('success', t('notifications.itemWasSuccessfullyDeleted'));
      setShowModal(false);
    } catch (e) {
      showGenericErrorToast();
    }
  };

  const handleCancel = () => {
    setChosenBranchOffice(null);
    setShowModal(false);
  };

  const updateFilter = async (
    key: string,
    value: string | boolean | undefined
  ) => {
    setFilter((filter) => updateFilterData(filter, { key, value }));
  };

  useEffect(() => {
    fetchAllBranchOffices()
      .then((branchOffices) => {
        const options = branchOffices.map((branchOffice) => ({
          value: branchOffice.id || 0,
          label: branchOffice.name || '',
        }));

        setBranchOfficesAll(options);
      })
      .catch(() => {
        showGenericErrorToast();
      });
  }, [showGenericErrorToast, t]);

  useEffect(() => {
    fetchData();
  }, [currentPage, fetchData, filter, rowsPerPage]);

  return (
    <BranchOfficeContext.Provider value={branchOfficesAll}>
      <CDataGrid
        colDef={branchOfficeSettingsColumnsDef}
        translationKey="branchOfficesSettingsPage"
        data={branchOffices}
        onAdd={handleAddBranchOffice}
        onDelete={handleDeleteBranchOffice}
        onEdit={(values) => handleEditBranchOffice(values as BranchOfficeDto)}
        filters={
          <BranchOfficesFilters updateFilter={updateFilter} filter={filter} />
        }
        changeRowsPerPage={(rowsPerPage: number) => setRowsPerPage(rowsPerPage)}
        onSort={(sortModel) => {
          setFilter((filter) => ({
            ...filter,
            sortBy: sortModel.sortBy,
            descending: sortModel.descending,
          }));
        }}
        pagination={{
          onPageChange: handleChangePage,
          totalCount: countOfBranchOffices,
          currentPage,
          pageSize: rowsPerPage,
        }}
      />
      <Modal
        open={showModal}
        title={t('navigation.branch-offices')}
        onClose={() => setShowModal(false)}
        type={chosenBranchOffice ? 'edit' : 'create'}
        width={1600}
      >
        <BranchOfficeForm
          branchOffice={chosenBranchOffice}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
        />
      </Modal>
    </BranchOfficeContext.Provider>
  );
};
export default SettingsBranchOfficePage;
